import {
  Fragment,
  SetStateAction,
  UIEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

// ** Third Party Components
import axios from "axios"
import { X } from "lucide-react"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { createPortal } from "react-dom"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragOverEvent,
  pointerWithin,
} from "@dnd-kit/core"

// ** Theme Component
import { Badge } from "@pma/components/common/badge"
import { Input } from "@pma/components/common/input"
import PmsLoader from "@pma/components/common/loader"
import { Button } from "@pma/components/common/button"
import { Card, CardHeader } from "@pma/components/common/card"
import { DataTablePagination } from "@pma/components/common/data-table/data-table-pagination"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@pma/components/common/accordion"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useAuth } from "@pma/stores/auth-store"
import { useDrawer } from "@pma/stores/drawer-store"

// ** Constants
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"

// ** Routes
import { EXPORT_ALL_TASKS, SERVER_URL } from "@pma/utils/api-routes"

// ** Context
import { GroupedTasksContext } from "./context/GroupedTasksProvider"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import { NewTask } from "./New-task"
import { AddGroup } from "./Add-Group"
import DeleteGroup from "./Delete-Group"
import TableSection from "./Table-Section"
import AddTaskInput from "../add-task-input"
import TaskCardView from "../task-card-view"
import { UpdateGroup } from "./Update-Group"
import { FilterGroups } from "./Group-filter"
import RestoreTaskGroup from "./Restore-task-group"
import ImportTaskDialog from "./Import-Task-dialog"
import TableBatchActions from "../table-batch-actions"
import { GroupViewOptions } from "./group-view-options"
import DraggableRow from "../graggable-tasks/DraggableRow"

interface Task {
  id: number
  tasks_group_id: number | null
  title?: string
  created_at?: string | number | Date
  _subtasks?: Subtask[]
  [key: string]: any
}

interface Subtask {
  id: number
  parent_tasks_id: number
  [key: string]: any
}

interface Group {
  id: number
  name: string
  project_id: number
  user_id: number
  _tasks: Task[]
  created_at: string | null
  deleted?: boolean
  deleted_by?: null | number
  total_counter: number
}

const GroupSection = () => {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { projectId } = useParams()
  const { translate, locale } = useTranslator()
  const [searchParam, setSearchParam] = useSearchParams()

  const hasScrolledRef = useRef<boolean>(false)
  const lastGlobalFilterRef = useRef<string>("")
  const groupRef = useRef<HTMLElement | null>(null)
  const highlightedTaskRef = useRef<HTMLElement | null>(null)
  const taskRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const {
    groupedTasks,
    groupedTasksLoading,
    selectedTasks,
    setSelectedTasks,
    updateGroupTasks,
    search,
    paginationData,
    setSearch,
    moveTaskToGroupMutation,
    lastInsertTaskId,
    getUserName,
    getTaskStatusName,
    groupedTasksRefetch,
    setPagination,
    taskPrioritiesList,
    pageSorting,
  } = useContext(GroupedTasksContext) as {
    groupedTasks: Group[]
    groupedTasksLoading: boolean
    groupedTasksRefetch: any
    selectedTasks: any[]
    paginationData: {
      curPage: number
      itemsReceived: number
      itemsTotal: number
      nextPage: number
      offset: number
      pageTotal: number
      prevPage: number
      pageSize: number
    }
    setPagination: (pagination: any) => void
    setSelectedTasks: (tasks: any[]) => void
    updateGroupTasks: (groupId: number, tasks: Task[]) => void
    search: string
    setSearch: (search: string) => void
    moveTaskToGroupMutation: {
      mutate: (data: {
        task_id: number
        target_group_id: number | null
        with_users: boolean
        inform_users: boolean
        project_id: number
      }) => void
      isSuccess: boolean
      variables: any
    }
    lastInsertTaskId: number
    getUserName: (activeId: string) => any
    getTaskStatusName: (statusList: string[]) => any
    taskPrioritiesList: any[]
    pageSorting: { orderBy: string; sortBy: string }
  }

  const colors = ["#A52A2A", "#F11010", "#F17010", "#F1D310", "#259F0A", "#147CE1", "#9914E1"]

  const drawer = useDrawer()
  const isHebrew = locale === "he"
  const pathname = location?.pathname
  const toDate = searchParam.get("to")
  const userId = String(authConfig?.id)
  const fromDate = searchParam.get("from")
  const activeUser = searchParam.get("user")
  const priority = searchParam.get("priority")
  const activeStatus = searchParam.get("status")
  const trackerStatus = searchParam.get("tracker")
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const showDeletedTasks = searchParam.get("showDeletedTasks")
  const activeFrom = fromDate ? new Date(parseInt(String(fromDate))) : undefined
  const activeTo = toDate ? new Date(parseInt(String(toDate))) : undefined
  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const highlightedTaskId = searchParam.get("task") ? parseInt(searchParam.get("task")!) : null
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const [globalFilter, setGlobalFilter] = useState("")
  const [activeRow, setActiveRow] = useState<any>(null)
  const [clearFilter, setClearFilter] = useState(false)
  const [isMobileDrag, setIsMobileDrag] = useState(false)
  const [activeTask, setActiveTask] = useState<any>(null)
  const [taskModal, setTaskModal] = useState<boolean>(false)
  const [accordionValue, setAccordionValue] = useState<string[]>([])
  const [targetGroupId, setTargetGroupId] = useState<number | null>(null)
  const [importTaskDialog, setImportTaskDialog] = useState<boolean>(false)
  const [hoverTimer, setHoverTimer] = useState<NodeJS.Timeout | null>(null)
  const [selectedGroupToEdit, setSelectedGroupToEdit] = useState<any>(null)
  const [searchExpandedGroups, setSearchExpandedGroups] = useState<string[]>([])
  const [tempHighlightedTaskId, setTempHighlightedTaskId] = useState<number | null>(null)
  const [tempTasksByGroup, setTempTasksByGroup] = useState<{ [groupId: number]: any[] }>({})
  const [groupEntryCounts, setGroupEntryCounts] = useState<[{ group_id: number; count: number }]>([
    { group_id: 0, count: 0 },
  ])

  // Read task from query parameters
  useEffect(() => {
    if (drawer.updateRecord) {
      groupedTasksRefetch()
      drawer.setUpdateRecord(false)
    }
  }, [drawer.updateRecord])

  useEffect(() => {
    const storedTargetGroupId = localStorage.getItem("target_group_id")
    if (storedTargetGroupId) {
      const groupId = parseInt(storedTargetGroupId)
      setTargetGroupId(groupId)
      // Expand the target group
      setAccordionValue((prev) => {
        if (!prev.includes(groupId.toString())) {
          return [...prev, groupId.toString()]
        }
        return prev
      })
    }
  }, [moveTaskToGroupMutation.isSuccess])

  useEffect(() => {
    const queryValue = searchParam.get("query")
    if (!queryValue && globalFilter !== "") {
      setGlobalFilter("")
      setSearch("")
    }
  }, [searchParam])

  // Auto-expand group containing the task
  useEffect(() => {
    if (highlightedTaskId && groupedTasks.length > 0) {
      const groupContainingTask = groupedTasks.find((group) =>
        group._tasks.some((task) => task.id === highlightedTaskId),
      )
      if (groupContainingTask && !accordionValue.includes(groupContainingTask.id.toString())) {
        setAccordionValue((prev) => [...prev, groupContainingTask.id.toString()])
      }
    }
  }, [highlightedTaskId, groupedTasks])

  // Scroll to the highlighted task after accordion is expanded
  useEffect(() => {
    if (tempHighlightedTaskId && accordionValue.length > 0) {
      // Wait for the DOM to update after accordion expansion
      const scrollTimeout = setTimeout(() => {
        const taskElement = document.querySelector(
          `[data-task-id="${tempHighlightedTaskId}"]`,
        ) as HTMLElement

        if (taskElement) {
          highlightedTaskRef.current = taskElement
          taskElement.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 200)
      return () => clearTimeout(scrollTimeout)
    }
  }, [tempHighlightedTaskId, accordionValue])

  useEffect(() => {
    if (highlightedTaskId) {
      setTempHighlightedTaskId(highlightedTaskId)

      const timer = setTimeout(() => {
        setTempHighlightedTaskId(null)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [highlightedTaskId])

  useEffect(() => {
    if (
      targetGroupId &&
      accordionValue.includes(targetGroupId.toString()) &&
      !hasScrolledRef.current
    ) {
      const scrollTimeout = setTimeout(() => {
        const groupElement = document.querySelector(
          `[data-group-id="${targetGroupId}"]`,
        ) as HTMLElement

        if (groupElement) {
          groupRef.current = groupElement
          groupElement.scrollIntoView({ behavior: "smooth", block: "center" })
          hasScrolledRef.current = true
          // Clear target_group_id from localStorage and state
          localStorage.removeItem("target_group_id")
          setTargetGroupId(null)
        }
      }, 200)
      return () => clearTimeout(scrollTimeout)
    }
  }, [targetGroupId, accordionValue])

  useEffect(() => {
    if (
      !groupedTasksLoading &&
      groupedTasks?.length > 0 &&
      globalFilter !== lastGlobalFilterRef.current
    ) {
      const searchLower = globalFilter.toLowerCase()
      const groupsWithTasks = groupedTasks
        //Filter groups with tasks or subtasks matching the search
        .filter((group) => {
          const matchingTasks = group._tasks?.filter((task) => {
            const taskIdFormatted = `PR-${task.id}`.toLowerCase()
            const taskMatches =
              taskIdFormatted.includes(searchLower) ||
              task.title?.toLowerCase().includes(searchLower) ||
              task.description?.toLowerCase().includes(searchLower)

            const subtaskMatches =
              task._subtasks?.some((subtask) => {
                const subtaskIdFormatted = `PR-${subtask.id}`.toLowerCase()
                return (
                  subtaskIdFormatted.includes(searchLower) ||
                  subtask.title?.toLowerCase().includes(searchLower) ||
                  subtask.description?.toLowerCase().includes(searchLower)
                )
              }) || false
            return taskMatches || subtaskMatches
          })
          return matchingTasks?.length > 0
        })
        .map((group) => group.id.toString())

      setSearchExpandedGroups(groupsWithTasks)
      setAccordionValue((prev) => {
        const collapsedGroupsWithTasks = groupsWithTasks.filter(
          (groupId) => !prev.includes(groupId),
        )

        const newValue = [...new Set([...prev, ...collapsedGroupsWithTasks])]
        // Update localStorage
        const storedState = localStorage.getItem("accordionState")
        let stateObj: { [userId: string]: { [projectId: string]: number[] } } = {}
        if (storedState) {
          try {
            stateObj = JSON.parse(storedState)
            if (!stateObj[userId]) stateObj[userId] = {}
          } catch (error) {
            console.error("Error parsing accordion state from localStorage:", error)
          }
        } else stateObj[userId] = {}
        const numericValues = newValue.map(Number).filter((id: number) => !isNaN(id))
        stateObj[userId][`project${projectId}`] = numericValues
        localStorage.setItem("accordionState", JSON.stringify(stateObj))
        return newValue
      })
      lastGlobalFilterRef.current = globalFilter
    } else if (!globalFilter) {
      setSearchExpandedGroups([])
      lastGlobalFilterRef.current = ""
    }
  }, [groupedTasks, groupedTasksLoading, projectId, userId])

  useEffect(() => {
    if (moveTaskToGroupMutation.isSuccess) hasScrolledRef.current = false
  }, [moveTaskToGroupMutation.isSuccess])

  useEffect(() => {
    return () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        setHoverTimer(null)
      }
    }
  }, [hoverTimer])

  // Load accordion state from localStorage on mount
  useEffect(() => {
    if (projectId && userId) {
      const storedState = localStorage.getItem("accordionState")
      let initialAccordionValue: string[] = []
      if (storedState) {
        try {
          const parsedState = JSON.parse(storedState)
          const userState = parsedState[userId]?.[`project${projectId}`]
          if (userState && Array.isArray(userState)) initialAccordionValue = userState.map(String)
        } catch (error) {
          // console.error("Error parsing accordion state from localStorage:", error)
        }
      }
      // Merge stored state with auto-expanded group
      if (highlightedTaskId && groupedTasks.length > 0) {
        const groupContainingTask = groupedTasks.find((group) =>
          group._tasks.some((task) => task.id === highlightedTaskId),
        )
        if (
          groupContainingTask &&
          !initialAccordionValue.includes(groupContainingTask.id.toString())
        ) {
          initialAccordionValue.push(groupContainingTask.id.toString())
        }
      }
      setAccordionValue(initialAccordionValue)
    }
  }, [projectId, userId, highlightedTaskId, groupedTasks])

  // Debounced save to localStorage
  const saveAccordionState = useCallback(() => {
    if (projectId && userId) {
      const storedState = localStorage.getItem("accordionState")
      let stateObj: { [userId: string]: { [projectId: string]: number[] } } = {}
      if (storedState) {
        try {
          stateObj = JSON.parse(storedState)
          if (typeof stateObj !== "object" || Array.isArray(stateObj)) {
            stateObj = {}
          }
        } catch (error) {
          // console.error("Error parsing existing accordion state from localStorage:", error)
        }
      }

      // Initialize user object if it doesn't exist
      if (!stateObj[userId]) stateObj[userId] = {}

      // Only store non-empty arrays
      const numericValues = accordionValue.map(Number).filter((id) => !isNaN(id))
      if (numericValues.length > 0) {
        stateObj[userId][`project${projectId}`] = numericValues
      } else {
        delete stateObj[userId][`project${projectId}`]
        // Remove user object if empty
        if (Object.keys(stateObj[userId]).length === 0) {
          delete stateObj[userId]
        }
      }

      // Save to localStorage if there are any states
      if (Object.keys(stateObj).length > 0)
        localStorage.setItem("accordionState", JSON.stringify(stateObj))
      else localStorage.removeItem("accordionState")
    }
  }, [accordionValue, projectId, userId])

  // Save with debouncing
  useEffect(() => {
    const timeout = setTimeout(saveAccordionState, 500)
    return () => clearTimeout(timeout)
  }, [saveAccordionState])

  const allTasksIds = useMemo(() => {
    const ids: string[] = []
    groupedTasks.forEach((group) => {
      ids.push(`group-header_${group.id}`)
      if (group._tasks.length === 0) ids.push(`empty_${group.id}`)
      else {
        group._tasks.forEach((task: Task) => {
          ids.push(`task_${task.id}_${group.id}`)
          if (task._subtasks && task._subtasks.length > 0) {
            task._subtasks.forEach((subtask: Subtask) => {
              ids.push(`subtask_${task.id}_${subtask.id}_${group.id}`)
            })
          }
        })
      }
    })
    return ids
  }, [groupedTasks])

  const groupTasksIds = useMemo(() => {
    const tasksIdsMap: { [groupId: number]: string[] } = {}
    if (!groupedTasks || !Array.isArray(groupedTasks)) return tasksIdsMap

    groupedTasks.forEach((group) => {
      const ids: string[] = []
      if (group._tasks && Array.isArray(group._tasks)) {
        group._tasks.forEach((task: any) => {
          if (task.id) {
            ids.push(`task_${task.id}_${group.id}`)
            if (Array.isArray(task.subtasks) && task._subtasks.length > 0) {
              task._subtasks.forEach((subtask: any) => {
                if (subtask.id) ids.push(`subtask_${task.id}_${subtask.id}_${group.id}`)
              })
            }
          }
        })
      }
      ids.push(`empty_${group.id}`)
      tasksIdsMap[group.id] = ids
    })
    return tasksIdsMap
  }, [groupedTasks])

  const getColor = (index: number) => {
    const mod = index % colors.length
    return colors[mod]
  }

  const handleEnableEditGroup = (group: any) => setSelectedGroupToEdit(group)

  const clearCurrentUserTableFormats = () => {
    setSearchParam((params) => {
      params.delete("user")
      params.delete("status")
      params.delete("from")
      params.delete("to")
      params.delete("tracker")
      params.delete("search")
      params.delete("priority")
      return params
    })
    navigate(pathname)
    setClearFilter(true)
  }

  const handleArrayToString = () => {
    const statusList = activeStatus?.split(",")
    if (statusList) {
      const statusName = getTaskStatusName(statusList)
      return `-${statusName.join("-")}`
    }
    return ""
  }

  const getPriorityName = (priorityId: string): string | null => {
    const found = taskPrioritiesList?.find((p) => p.id.toString() === priorityId.toString())
    return found?.name ?? null
  }

  const statusFilterKey = handleArrayToString()
  const userFilterKey = `${activeUser ? `-${getUserName(activeUser)}` : ""}`
  const globalFilterKey = `${globalFilter ? `-${globalFilter}` : ""}`
  const trackerFilterKey = `${trackerStatus ? `-${trackerStatus}` : ""}`
  const showDeletedTasksFilterKey = `${showDeletedTasks ? `-${translate("Show Deleted Tasks")}` : ""}`
  const priorityFilterKey = `${priority ? `-${getPriorityName(priority) || priority}` : ""}`

  const dateRangeFilterKey = activeFrom
    ? `-${format(activeFrom, "LLL dd, y", { locale: isHebrew ? he : undefined })} to ` +
    (activeTo ? format(activeTo, "LLL dd, y", { locale: isHebrew ? he : undefined }) : "")
    : ""

  const dateRangeKey = activeFrom
    ? `-${activeFrom.getTime()} to ` + (activeTo ? activeTo.getTime() : "")
    : ""

  const searchKey =
    globalFilterKey +
    statusFilterKey +
    userFilterKey +
    dateRangeKey +
    trackerFilterKey +
    priorityFilterKey +
    showDeletedTasksFilterKey

  const searchKeyUnformatted =
    globalFilterKey +
    statusFilterKey +
    userFilterKey +
    dateRangeFilterKey +
    trackerFilterKey +
    priorityFilterKey +
    showDeletedTasksFilterKey

  const searchKeyArray = searchKeyUnformatted
    .split("-")
    .filter((value) => value !== "")
    .filter((value2) => value2 !== "")

  useEffect(() => {
    if (searchKeyArray.length > 0) setClearFilter(false)
  }, [searchKeyArray])

  function debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number,
  ): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    let timeoutId: NodeJS.Timeout | null = null

    const debounced = function (this: any, ...args: Parameters<T>) {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        func.apply(this, args)
        timeoutId = null
      }, delay)
    }

    debounced.cancel = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    return debounced as ((...args: Parameters<T>) => void) & { cancel: () => void }
  }

  const handleSearch = useCallback((searchString: string) => {
    setSearch(searchString)
  }, [])

  const debounceFunction = useCallback(debounce(handleSearch, 800), [handleSearch])

  useEffect(() => {
    setSearchParam((params) => {
      if (globalFilter) params.set("query", globalFilter)
      else params.delete("query")

      return params
    })
    debounceFunction(globalFilter)
  }, [globalFilter])

  function isMobile() {
    const minWidth = 768
    return window.innerWidth < minWidth || screen.width < minWidth
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(require("@dnd-kit/core").TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
  )

  // Table View Handlers
  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "Task") {
      const row = event.active.data.current.row
      const task = event.active.data.current.task
      setActiveTask(task || row?.original)
      setActiveRow(row)
      setIsMobileDrag(false)
    }
  }

  function onDragOver(event: DragOverEvent) {
    const { over } = event
    if (!over) return

    const overId = over.id.toString()
    if (overId.startsWith("group-header_")) {
      const groupId = parseInt(overId.split("_")[1])
      const isGroupOpen = accordionValue.includes(groupId.toString())
      if (!isGroupOpen && !hoverTimer) {
        const timer = setTimeout(() => {
          setAccordionValue((prev) => [...prev, groupId.toString()])
          setHoverTimer(null)
        }, 1000)
        setHoverTimer(timer)
      }
    } else if (hoverTimer) {
      clearTimeout(hoverTimer)
      setHoverTimer(null)
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveTask(null)
    setActiveRow(null)
    setTempTasksByGroup({})
    if (hoverTimer) {
      clearTimeout(hoverTimer)
      setHoverTimer(null)
    }
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const overId = over.id.toString()

    const activeGroupId =
      activeId.includes("task_") || activeId.includes("subtask_")
        ? parseInt(activeId.split("_").pop() || "NaN")
        : NaN
    const overGroupId = overId.includes("group-header_")
      ? parseInt(overId.split("_")[1])
      : overId.includes("empty_")
        ? parseInt(overId.split("_")[1])
        : overId.includes("task_") || overId.includes("subtask_")
          ? parseInt(overId.split("_").pop() || "NaN")
          : NaN

    if (isNaN(activeGroupId) || isNaN(overGroupId)) return

    if (activeId.startsWith("subtask_")) {
      const [, parentId, activeTaskId, groupId] = activeId.split("_").map((s) => parseInt(s))
      const [, overParentId, overTaskId] = overId.startsWith("subtask_")
        ? overId.split("_").map((s) => parseInt(s))
        : [, , parseInt(overId.split("_")[1])]

      if (isNaN(groupId) || (overId.startsWith("subtask_") && parentId !== overParentId)) return

      const group = groupedTasks.find((g) => g.id === groupId)
      if (!group) return

      const parentTaskIndex = group._tasks.findIndex((task: Task) => task.id === parentId)
      if (parentTaskIndex === -1) return

      const parentTask = group._tasks[parentTaskIndex]
      const subtasks = [...(parentTask._subtasks || [])]
      const oldIndex = subtasks.findIndex((task: Subtask) => task.id === activeTaskId)
      const newIndex = overId.startsWith("subtask_")
        ? subtasks.findIndex((task: Subtask) => task.id === overTaskId)
        : subtasks.length

      if (oldIndex === -1 || newIndex === -1) return

      const reorderedSubtasks = arrayMove(subtasks, oldIndex, newIndex)
      const newTasks = [...group._tasks]
      newTasks[parentTaskIndex] = { ...parentTask, _subtasks: reorderedSubtasks }

      updateGroupTasks(groupId, newTasks)
    } else if (activeId.startsWith("task_")) {
      const activeTaskId = parseInt(activeId.split("_")[1])
      const sourceGroup = groupedTasks.find((g) => g.id === activeGroupId)
      if (!sourceGroup) return

      const task = sourceGroup._tasks.find((t: Task) => t.id === activeTaskId)
      if (!task) return

      const updatedTask = { ...task, tasks_group_id: overGroupId === -1 ? null : overGroupId }
      const sourceTasks = sourceGroup._tasks.filter((t: Task) => t.id !== activeTaskId)

      if (overId.startsWith("group-header_") || overId.startsWith("empty_")) {
        if (overGroupId === -1 && activeGroupId !== overGroupId) return

        const targetGroup = groupedTasks.find((g) => g.id === overGroupId)
        if (!targetGroup) return

        const targetTasks = [...targetGroup._tasks, updatedTask]
        setTimeout(() => {
          updateGroupTasks(activeGroupId, sourceTasks)
          updateGroupTasks(overGroupId, targetTasks)
          if (moveTaskToGroupMutation) {
            moveTaskToGroupMutation.mutate({
              task_id: activeTaskId,
              target_group_id: overGroupId === -1 ? null : overGroupId,
              with_users: false,
              inform_users: false,
              project_id: parseInt(projectId || "0"),
            })
          }
        }, 100)
      } else if (activeGroupId === overGroupId) {
        const group = groupedTasks.find((g) => g.id === activeGroupId)
        if (!group) return

        const oldIndex = group._tasks.findIndex((task: Task) => task.id === activeTaskId)
        const overTaskId = overId.startsWith("task_") ? parseInt(overId.split("_")[1]) : null
        const newIndex = overTaskId
          ? group._tasks.findIndex((task: Task) => task.id === overTaskId)
          : group._tasks.length - 1

        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

        const newTasks = arrayMove(group._tasks, oldIndex, newIndex)
        updateGroupTasks(activeGroupId, newTasks)
      } else {
        if (overGroupId === -1) return
        const targetGroup = groupedTasks.find((g) => g.id === overGroupId)
        if (!targetGroup) return

        let targetTasks = [...targetGroup._tasks]
        const overTaskId = overId.startsWith("task_") ? parseInt(overId.split("_")[1]) : null
        if (overTaskId) {
          const targetIndex = targetTasks.findIndex((t: Task) => t.id === overTaskId)
          targetTasks.splice(targetIndex, 0, updatedTask)
        } else targetTasks.push(updatedTask)

        updateGroupTasks(activeGroupId, sourceTasks)
        updateGroupTasks(overGroupId, targetTasks)

        if (moveTaskToGroupMutation) {
          moveTaskToGroupMutation.mutate({
            task_id: activeTaskId,
            target_group_id: overGroupId === -1 ? null : overGroupId,
            with_users: false,
            inform_users: false,
            project_id: parseInt(projectId || "0"),
          })
        }
      }
    }
  }

  function onDragStartM(event: DragStartEvent) {
    if (event.active.data.current?.type === "Task") {
      const task = event.active.data.current.task
      setActiveTask(task)
      setActiveRow(null)
      setIsMobileDrag(true)
    }
  }

  function onDragEndM(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const overId = over.id.toString()

    const activeGroupId =
      activeId.includes("task_") || activeId.includes("subtask_")
        ? parseInt(activeId.split("_").pop() || "NaN")
        : NaN
    const overGroupId = overId.includes("group-header_")
      ? parseInt(overId.split("_")[1])
      : overId.includes("empty_")
        ? parseInt(overId.split("_")[1])
        : overId.includes("task_") || overId.includes("subtask_")
          ? parseInt(overId.split("_").pop() || "NaN")
          : NaN

    if (isNaN(activeGroupId) || isNaN(overGroupId)) return

    if (activeId.startsWith("subtask_")) {
      const activeParts = activeId.split("_").map((s) => parseInt(s))
      const overParts = overId.startsWith("subtask_")
        ? overId.split("_").map((s) => parseInt(s))
        : [, , parseInt(overId.split("_")[2])]

      const [, parentId, activeTaskId, groupId] = activeParts
      const [, overParentId, overTaskId] = overParts

      // Restrict subtask to same parent task
      if (isNaN(groupId) || (overId.startsWith("subtask_") && parentId !== overParentId)) {
        setTempTasksByGroup({})
        setActiveTask(null)
        setActiveRow(null)
        setIsMobileDrag(false)
        if (hoverTimer) {
          clearTimeout(hoverTimer)
          setHoverTimer(null)
        }
        return
      }

      const group = groupedTasks.find((g) => g.id === groupId)
      if (!group) {
        setTempTasksByGroup({})
        setActiveTask(null)
        setActiveRow(null)
        setIsMobileDrag(false)
        if (hoverTimer) {
          clearTimeout(hoverTimer)
          setHoverTimer(null)
        }
        return
      }

      const parentTaskIndex = group._tasks.findIndex((task: Task) => task.id === parentId)
      if (parentTaskIndex === -1) {
        setTempTasksByGroup({})
        setActiveTask(null)
        setActiveRow(null)
        setIsMobileDrag(false)
        if (hoverTimer) {
          clearTimeout(hoverTimer)
          setHoverTimer(null)
        }
        return
      }

      const parentTask = group._tasks[parentTaskIndex]
      const subtasks = [...(parentTask._subtasks || [])]
      const oldIndex = subtasks.findIndex((task: Subtask) => task.id === activeTaskId)
      const newIndex = overId.startsWith("subtask_")
        ? subtasks.findIndex((task: Subtask) => task.id === overTaskId)
        : subtasks.length - 1

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        setTempTasksByGroup({})
        setActiveTask(null)
        setActiveRow(null)
        setIsMobileDrag(false)
        if (hoverTimer) {
          clearTimeout(hoverTimer)
          setHoverTimer(null)
        }
        return
      }

      const reorderedSubtasks = arrayMove(subtasks, oldIndex, newIndex)
      const newTasks = [...group._tasks]
      newTasks[parentTaskIndex] = { ...parentTask, _subtasks: reorderedSubtasks }

      // Update state and clear temp state
      updateGroupTasks(groupId, newTasks)
      setTempTasksByGroup({})
      setActiveTask(null)
      setActiveRow(null)
      setIsMobileDrag(false)
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        setHoverTimer(null)
      }
    } else if (activeId.startsWith("task_")) {
      const activeTaskId = parseInt(activeId.split("_")[1])
      const sourceGroup = groupedTasks.find((g) => g.id === activeGroupId)
      if (!sourceGroup) {
        setTempTasksByGroup({})
        setActiveTask(null)
        setActiveRow(null)
        setIsMobileDrag(false)
        if (hoverTimer) {
          clearTimeout(hoverTimer)
          setHoverTimer(null)
        }
        return
      }

      const task = sourceGroup._tasks.find((t: Task) => t.id === activeTaskId)
      if (!task) {
        setTempTasksByGroup({})
        setActiveTask(null)
        setActiveRow(null)
        setIsMobileDrag(false)
        if (hoverTimer) {
          clearTimeout(hoverTimer)
          setHoverTimer(null)
        }
        return
      }

      const updatedTask = { ...task, tasks_group_id: overGroupId === -1 ? null : overGroupId }
      const sourceTasks = sourceGroup._tasks.filter((t: Task) => t.id !== activeTaskId)

      if (overId.startsWith("group-header_") || overId.startsWith("empty_")) {
        if (overGroupId === -1 && activeGroupId !== overGroupId) {
          setTempTasksByGroup({})
          setActiveTask(null)
          setActiveRow(null)
          setIsMobileDrag(false)
          if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
          }
          return
        }

        const targetGroup = groupedTasks.find((g) => g.id === overGroupId)
        if (!targetGroup) {
          setTempTasksByGroup({})
          setActiveTask(null)
          setActiveRow(null)
          setIsMobileDrag(false)
          if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
          }
          return
        }

        const targetTasks = [...targetGroup._tasks, updatedTask]
        updateGroupTasks(activeGroupId, sourceTasks)
        updateGroupTasks(overGroupId, targetTasks)
        if (moveTaskToGroupMutation) {
          moveTaskToGroupMutation.mutate({
            task_id: activeTaskId,
            target_group_id: overGroupId === -1 ? null : overGroupId,
            with_users: false,
            inform_users: false,
            project_id: parseInt(projectId || "0"),
          })
        }
      } else if (activeGroupId === overGroupId) {
        const group = groupedTasks.find((g) => g.id === activeGroupId)
        if (!group) {
          setTempTasksByGroup({})
          setActiveTask(null)
          setActiveRow(null)
          setIsMobileDrag(false)
          if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
          }
          return
        }

        const oldIndex = group._tasks.findIndex((task: Task) => task.id === activeTaskId)
        const overTaskId = overId.startsWith("task_") ? parseInt(overId.split("_")[1]) : null
        const newIndex = overTaskId
          ? group._tasks.findIndex((t: Task) => t.id === overTaskId)
          : group._tasks.length - 1

        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          setTempTasksByGroup({})
          setActiveTask(null)
          setActiveRow(null)
          setIsMobileDrag(false)
          if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
          }
          return
        }

        const newTasks = arrayMove(group._tasks, oldIndex, newIndex)
        updateGroupTasks(activeGroupId, newTasks)
      } else {
        if (overGroupId === -1) {
          setTempTasksByGroup({})
          setActiveTask(null)
          setActiveRow(null)
          setIsMobileDrag(false)
          if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
          }
          return
        }
        const targetGroup = groupedTasks.find((g) => g.id === overGroupId)
        if (!targetGroup) {
          setTempTasksByGroup({})
          setActiveTask(null)
          setActiveRow(null)
          setIsMobileDrag(false)
          if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
          }
          return
        }

        let targetTasks = [...targetGroup._tasks]
        const overTaskId = overId.startsWith("task_") ? parseInt(overId.split("_")[1]) : null
        if (overTaskId) {
          const targetIndex = targetTasks.findIndex((t: Task) => t.id === overTaskId)
          targetTasks.splice(targetIndex, 0, updatedTask)
        } else {
          targetTasks.push(updatedTask)
        }

        updateGroupTasks(activeGroupId, sourceTasks)
        updateGroupTasks(overGroupId, targetTasks)

        if (moveTaskToGroupMutation) {
          moveTaskToGroupMutation.mutate({
            task_id: activeTaskId,
            target_group_id: overGroupId === -1 ? null : overGroupId,
            with_users: false,
            inform_users: false,
            project_id: parseInt(projectId || "0"),
          })
        }
      }

      // Clear temp state for tasks
      setTempTasksByGroup({})
      setActiveTask(null)
      setActiveRow(null)
      setIsMobileDrag(false)
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        setHoverTimer(null)
      }
    }
  }

  const handleClearSelection = () => {
    setSelectedTasks([])
    queryClient.invalidateQueries({ queryKey: ["clear-selected-tasks"] })
    queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
  }

  const handleAccordion = useCallback(
    (group_id: string) => {
      const storedState = localStorage.getItem("accordionState")
      let stateObj: { [userId: string]: { [projectId: string]: number[] } } = {}
      let newAccordionValue: string[]
      if (accordionValue.includes(group_id)) {
        newAccordionValue = accordionValue.filter((id) => id !== group_id)
      } else newAccordionValue = [...accordionValue, group_id]

      const numericValues = newAccordionValue.map(Number).filter((id: number) => !isNaN(id))
      if (storedState) {
        try {
          stateObj = JSON.parse(storedState)
          if (!stateObj[userId]) stateObj[userId] = {}
          stateObj[userId][`project${projectId}`] = numericValues
          localStorage.setItem("accordionState", JSON.stringify(stateObj))
        } catch (error) {
          console.error("Error updating accordion state in localStorage:", error)
        }
      } else {
        stateObj[userId] = { [`project${projectId}`]: numericValues }
        localStorage.setItem("accordionState", JSON.stringify(stateObj))
      }
      setAccordionValue(newAccordionValue)
    },
    [accordionValue, projectId, userId],
  )

  const handleAccordionChange = (newValue: string[]) => {
    // Only update accordionValue with user-triggered changes
    setAccordionValue(newValue)
    // Save to localStorage
    const storedState = localStorage.getItem("accordionState")
    let stateObj: { [userId: string]: { [projectId: string]: number[] } } = {}
    if (storedState) {
      try {
        stateObj = JSON.parse(storedState)
        if (!stateObj[userId]) stateObj[userId] = {}
      } catch (error) {
        console.error("Error parsing accordion state from localStorage:", error)
      }
    } else stateObj[userId] = {}
    const numericValues = newValue.map(Number).filter((id: number) => !isNaN(id))
    stateObj[userId][`project${projectId}`] = numericValues
    localStorage.setItem("accordionState", JSON.stringify(stateObj))
  }

  const handleRef = (id: string, node: HTMLDivElement | null) => (taskRefs.current[id] = node)

  useEffect(() => {
    if (lastInsertTaskId && taskRefs.current[lastInsertTaskId]) {
      const node = taskRefs.current[lastInsertTaskId]
      node?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [lastInsertTaskId])

  useEffect(() => {
    if (!groupedTasksLoading) setGroupEntryCounts([{ group_id: 0, count: 0 }])
  }, [groupedTasksLoading])

  const scrollContainersRef = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const fakeScrollbarRef = useRef<HTMLDivElement>(null)

  const onFakeScrollbarScroll = (e: UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft
    Object.values(scrollContainersRef.current).forEach((el) => {
      if (el) el.scrollLeft = scrollLeft
    })
  }

  const onAccordionContentScroll = (groupId: string, e: UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft
    if (fakeScrollbarRef.current) fakeScrollbarRef.current.scrollLeft = scrollLeft

    Object.entries(scrollContainersRef.current).forEach(([id, el]) => {
      if (id !== groupId && el) el.scrollLeft = scrollLeft
    })
  }

  const handleToggleGroup = () => {
    let data: SetStateAction<string[]> = []
    const storedState = localStorage.getItem("accordionState")
    let stateObj: { [userId: string]: { [projectId: string]: number[] } } = {}
    if (storedState) {
      try {
        stateObj = JSON.parse(storedState)
        if (!stateObj[userId]) stateObj[userId] = {}
      } catch (error) {
        console.error("Error parsing accordion state from localStorage:", error)
      }
    } else stateObj[userId] = {}

    if (accordionValue.length === 0) {
      const groupIds = groupedTasks.map((group) => `${group.id}`)
      data = groupIds
      const numericValues = groupIds.map(Number).filter((id: number) => !isNaN(id))
      stateObj[userId][`project${projectId}`] = numericValues
    } else {
      stateObj[userId][`project${projectId}`] = []
    }
    setAccordionValue(data)
    localStorage.setItem("accordionState", JSON.stringify(stateObj))
  }

  const exportAllTasksApi = async () => {
    const payload: any = {
      project_id: projectId ? parseInt(projectId) : 0,
    }

    if (activeStatus) {
      const statusList = activeStatus.split(",")
      payload["status_id"] = [parseInt(statusList[0])]
    }

    if (activeUser) payload["responsible_id"] = [parseInt(activeUser)]

    if (pageSorting.sortBy) payload["sorting"] = pageSorting

    if (search) payload["search_text"] = search

    if (trackerStatus) payload["by_active_sessions"] = trackerStatus == "Running" ? true : false

    if (showDeletedTasks) payload["show_deleted"] = showDeletedTasks === "true" ? true : false

    if (fromDate && toDate) {
      // Ensure fromDate and toDate are always Date objects
      const parseToDate = (value: string | null): Date => {
        if (!value) return new Date()
        // If it's a number string, treat as timestamp
        if (!isNaN(Number(value))) {
          return new Date(Number(value))
        }
        // Otherwise, try to parse as ISO/date string
        const parsed = new Date(value)
        if (isNaN(parsed.getTime())) {
          throw new Error(`Invalid date: ${value}`)
        }
        return parsed
      }
      const stateDate: Date = parseToDate(fromDate)
      const endDate: Date = parseToDate(toDate)

      payload["start_at_period"] = {
        from: format(stateDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
        to: format(endDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
      }

      payload["finish_to_period"] = {
        from: format(stateDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
        to: format(endDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
      }

      payload["created_at_period"] = {
        from: format(stateDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
        to: format(endDate, "yyyy-MM-dd", { locale: isHebrew ? he : undefined }),
      }
    }

    if (priority) payload["priority_id"] = [parseInt(priority)]

    if (selectedTasks.length) payload["tasks_id"] = selectedTasks.map((task) => task.taskId)

    const response = await axios.post(`${SERVER_URL}${EXPORT_ALL_TASKS}`, payload, {
      responseType: "arraybuffer",
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
    })

    return response.data
  }

  const exportAllTasksMutation = useMutation({
    mutationKey: ["export-all-tasks"],
    mutationFn: exportAllTasksApi,
    onSuccess: (data) => {
      if (selectedTasks.length) setSelectedTasks([])

      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url

      const dateStr = format(new Date(), "yyyy-MM-dd_HH-mm")
      link.download = `tasks_export_${dateStr}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to export tasks"),
        variant: "error",
      })
    },
  })

  return (
    <Fragment>
      <div className='mx-2 flex flex-wrap'>
        <div className='w-full px-2 md:w-full xl:w-1/3'>
          <AddGroup />
          <hr className='my-3 md:hidden' />
        </div>
        <div className='w-full px-2 md:w-full xl:w-2/3'>
          <div className='flex flex-wrap gap-2 xl:justify-end'>
            <div className='relative order-1 w-full sm:w-auto md:w-full lg:order-2 lg:w-1/3'>
              <Input
                placeholder={`${translate("Search")}...`}
                value={globalFilter}
                onChange={(event) => setGlobalFilter(event.target.value)}
                className='h-8 md:mb-0'
              />
              {groupedTasksLoading && (
                <div className={`absolute top-[5px] ${isHebrew ? "left-[10px]" : "right-[10px]"}`}>
                  <PmsLoader className='h-[30px]' />
                </div>
              )}
            </div>
            <div className='order-5 w-full sm:w-auto lg:order-1'>
              <Button
                className='w-full sm:w-auto'
                size='sm'
                onClick={() => setImportTaskDialog(true)}
              >
                {translate("Import Tasks")}
              </Button>
            </div>
            <div className='order-5 w-full sm:w-auto lg:order-1'>
              <Button
                className='w-full sm:w-auto'
                size='sm'
                onClick={() => exportAllTasksMutation.mutate()}
                disabled={exportAllTasksMutation.isPending}
                loading={exportAllTasksMutation.isPending}
              >
                {translate("Export Tasks")}
              </Button>
            </div>
            <div className='order-5 w-full sm:w-auto lg:order-1'>
              <hr className='mb-3 md:hidden' />
              <Button className='w-full sm:w-auto' size='sm' onClick={handleToggleGroup}>
                {translate("Toggle Groups")}
              </Button>
            </div>
            <div className='order-5 w-full sm:w-auto lg:order-1'>
              <hr className='mb-3 md:hidden' />
              <Button
                className='w-full sm:w-auto'
                size='sm'
                onClick={() => setTaskModal(!taskModal)}
              >
                {translate("Add New Task")}
              </Button>
            </div>
            <div className='order-2 w-full sm:w-auto'>
              <FilterGroups clearFilter={clearFilter} />
            </div>
            <div className='order-3 hidden w-full sm:w-auto lg:flex'>
              <GroupViewOptions projectId={projectId} context={"tasks"} />
            </div>
            <div className='order-4 w-full sm:w-auto'>
              <Button
                variant='outline'
                size='sm'
                className='h-8 w-full sm:w-auto'
                onClick={clearCurrentUserTableFormats}
              >
                {translate("Clear Formats")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {searchKey ? (
        <div className='my-3 ml-3 flex items-center justify-end gap-2 pe-2 transition-all ease-in-out'>
          <div className='flex items-center gap-2'>
            <small>{translate("Active filter")}:</small>
            {searchKeyArray.map((searchItem, index) => (
              <Badge
                variant={index % 2 === 1 ? "outline" : "default"}
                className='font-thin'
                key={index}
              >
                {searchItem}
              </Badge>
            ))}
          </div>
          <X
            size={18}
            className='cursor-pointer text-red-500'
            onClick={() => {
              setGlobalFilter("")
              navigate(pathname)
              setClearFilter(true)
            }}
          />
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={isMobile() ? onDragStartM : onDragStart}
        onDragOver={onDragOver}
        onDragEnd={isMobile() ? onDragEndM : onDragEnd}
      >
        <SortableContext items={allTasksIds} strategy={verticalListSortingStrategy}>
          <div className='relative h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden'>
            <Accordion
              type='multiple'
              className='flex flex-col gap-3 pt-3'
              value={accordionValue}
              onValueChange={handleAccordionChange}
              key={accordionValue.join(",")}
            >
              {groupedTasks.map((group, index) => {
                const color = getColor(index + 1)
                const tasksIds = groupTasksIds[group.id] || []
                const displayTasks = tempTasksByGroup[group.id] || group._tasks
                const groupTaskCount = groupEntryCounts.find((val) => val.group_id === group.id)
                const taskCounter = groupTaskCount ? groupTaskCount.count : group.total_counter
                return (
                  <AccordionItem
                    key={group.id}
                    value={group.id.toString()}
                    className={cn(
                      "rounded-xl border px-2",
                      group?.deleted ? "bg-red-100" : "",
                      isHebrew ? "border-r-8" : "border-l-8",
                    )}
                    style={isHebrew ? { borderRightColor: color } : { borderLeftColor: color }}
                    data-group-id={group.id}
                  >
                    <AccordionTrigger
                      className={cn("py-1 hover:no-underline")}
                      sortableId={`group-header_${group.id}`}
                      onKeyDown={(e) => {
                        if (e.code === "Space" || e.key === " ") {
                          e.stopPropagation()
                          e.preventDefault() // Prevent default space toggle behavior
                        }
                      }}
                    >
                      <div className='flex w-full justify-between'>
                        <div className='flex w-full flex-col items-start gap-1'>
                          {selectedGroupToEdit && selectedGroupToEdit?.id === group.id ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <UpdateGroup
                                data={selectedGroupToEdit}
                                setSelectedGroupToEdit={setSelectedGroupToEdit}
                              />
                            </div>
                          ) : (
                            <div
                              className={cn(
                                "align-center flex justify-between gap-2 rounded-sm outline-[2px] outline-offset-4 outline-[#cccccc]",
                                { "hover:outline": group.id > 0 },
                              )}
                            >
                              <h3
                                className='text-lg'
                                style={{ color: color }}
                                onClick={(e) => {
                                  if (group.id > 0) {
                                    e.stopPropagation()
                                    handleEnableEditGroup(group)
                                  }
                                }}
                              >
                                {translate(group?.name)}
                              </h3>
                            </div>
                          )}
                          <div className='align-center relative flex w-full justify-between'>
                            <span className='text-sm font-normal'>
                              {taskCounter} {translate("Tasks")}
                            </span>
                          </div>
                        </div>
                        <div
                          className={`flex-end flex justify-between ${group.id === -1 ? "pe-2 sm:w-[300px]" : "pe-2 sm:w-[250px]"
                            }`}
                        >
                          {(globalFilter || activeUser || activeStatus || trackerStatus) && (
                            <div className={`flex flex-wrap items-center`}>
                              <span className='text-wrap text-orange-500'>
                                {translate("Open task group to see results")}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className='flex-end flex'>
                          {group.id !== -1 && (
                            <>
                              {group?.deleted &&
                                (isOrganizationOwner ||
                                  isAdmin ||
                                  group?.deleted_by == authConfig.id) ? (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  className={`me-1 self-center`}
                                >
                                  <RestoreTaskGroup group={group} />
                                </div>
                              ) : !group?.deleted ? (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  className={`me-1 self-center`}
                                >
                                  <DeleteGroup group={{ id: group?.id, name: group?.name }} />
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div
                        className='overflow-x-auto'
                        ref={(el) => (scrollContainersRef.current[group.id] = el)}
                        onScroll={(e) => onAccordionContentScroll(group.id.toString(), e)}
                      >
                        <div
                          className={
                            isMobile() ? "w-auto" : "w-[300px] sm:w-auto md:min-w-[3000px]"
                          }
                        >
                          {displayTasks?.length > 0 ? (
                            isMobile() ? (
                              <SortableContext
                                items={tasksIds}
                                strategy={verticalListSortingStrategy}
                              >
                                <div className='task-list-container flex flex-col gap-2'>
                                  {displayTasks.map((val: any, index: number) =>
                                    val.id ? (
                                      <TaskCardView
                                        task={val}
                                        key={`task-${val.id}`}
                                        index={index}
                                        group_id={group.id}
                                        onRef={handleRef}
                                      />
                                    ) : null,
                                  )}
                                  <AddTaskInput projectId={projectId} group_id={group.id} />
                                  {group.id == -1 && paginationData && (
                                    <DataTablePagination
                                      data={displayTasks}
                                      paginationData={paginationData}
                                      setPagination={setPagination}
                                    />
                                  )}
                                </div>
                              </SortableContext>
                            ) : (
                              <TableSection
                                isLoading={groupedTasksLoading}
                                tasks={group._tasks}
                                projectId={projectId ?? ""}
                                group_id={group.id}
                                searchKey={searchKey}
                                taskCounter={taskCounter}
                                setGroupEntryCounts={setGroupEntryCounts}
                                groupEntryCounts={groupEntryCounts}
                                highlightedTaskId={tempHighlightedTaskId}
                              />
                            )
                          ) : isMobile() ? (
                            <AddTaskInput projectId={projectId} group_id={group.id} />
                          ) : (
                            <TableSection
                              isLoading={groupedTasksLoading}
                              tasks={group._tasks}
                              projectId={projectId ?? ""}
                              group_id={group.id}
                              taskCounter={taskCounter}
                              searchKey={searchKey}
                              setGroupEntryCounts={setGroupEntryCounts}
                              groupEntryCounts={groupEntryCounts}
                              highlightedTaskId={tempHighlightedTaskId}
                            />
                          )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )
              })}
            </Accordion>
            {!isMobile() && (
              <div className='sticky bottom-0 left-0 right-0 z-10 h-4 border-t bg-white'>
                <div
                  ref={fakeScrollbarRef}
                  onScroll={onFakeScrollbarScroll}
                  className='scrollbar-hide h-full overflow-x-auto'
                >
                  <div className='h-1 w-[300px] sm:w-auto md:min-w-[3000px]' />
                </div>
              </div>
            )}
          </div>
        </SortableContext>
        {createPortal(
          <DragOverlay>
            {activeTask &&
              (isMobileDrag ? (
                <Card className='h-fit opacity-50 shadow-lg'>
                  <CardHeader className='flex flex-row items-center justify-between px-5 py-3'>
                    <div className='flex w-full flex-row gap-5 sm:w-[200px] md:w-[230px]'>
                      <div>
                        <p className='text-xs font-semibold'>{translate(`Ticket`)}</p>
                        <p className='text-xs text-slate-500'>{`${translate("PR")}-${activeTask.id}`}</p>
                      </div>
                      <div>
                        <p className='text-xs font-semibold'>{translate("Title")}</p>
                        <p className='w-[11ch] truncate text-ellipsis text-xs text-slate-500 sm:w-[8ch] md:w-[22ch]'>
                          {activeTask?.title}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ) : (
                <DraggableRow
                  row={activeRow}
                  getCommonPinningStyles={() => ({})}
                  group_id={activeTask.tasks_group_id}
                />
              ))}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
      {selectedTasks.length > 0 && (
        <TableBatchActions tasks={selectedTasks} onCancel={handleClearSelection} />
      )}

      {taskModal && (
        <NewTask
          handleAccordion={handleAccordion}
          taskModal={taskModal}
          setTaskModal={setTaskModal}
        />
      )}

      {importTaskDialog && (
        <ImportTaskDialog
          projectId={projectId || ""}
          importTaskDialog={importTaskDialog}
          setImportTaskDialog={setImportTaskDialog}
          onSuccess={groupedTasksRefetch}
        />
      )}
    </Fragment>
  )
}

export default GroupSection
